import { useState } from 'react';
import { ChevronDown, Minus, Plus } from 'lucide-react';
import { Segmented, SelectionGroup } from '@scripsteam/scrips-signal-ds';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import {
  BLOCK_PERIOD_DAYS,
  BLOCK_PERIOD_MONTHS,
  BLOCK_PERIOD_WEEKS,
  BLOCK_WEEK_DAYS,
  type BlockRecurring,
  type BlockRecurringPeriod,
  type BlockWeekDay,
} from '@/types/block-availability-api';

/**
 * BlockAvailabilityRecurringPicker — Flutter parity with
 * `recurring_widget.dart` (`repetitionView()`).
 *
 * Layout (top → bottom, only rendered when `state.recurring !== null`):
 *   - REPEAT EVERY stepper (integer ≥ 1)
 *   - PERIOD tag-picker (Day / Week / Month — wire values are the
 *     plural `"Days"` / `"Weeks"` / `"Months"`)
 *   - REPEAT ON weekday chips (only when period === "Weeks")
 *   - Info text "Every 4 weeks (exactly 28 days)." (period === "Months")
 *   - END DATE picker (today .. today + 100yrs in Flutter; we cap at
 *     today + 5yrs to give the popover calendar a sane scroll range
 *     without restricting realistic use)
 *
 * IgnorePointer parity: when `disabled` is true (PR 5 edit mode) every
 * control is read-only.
 */

interface Props {
  recurring: BlockRecurring;
  /** Block's anchor date — used to seed the auto-selected weekday on
   *  period flip to Weeks. */
  blockDate: Date;
  onPatch: (patch: Partial<BlockRecurring>) => void;
  onChangePeriod: (period: BlockRecurringPeriod) => void;
  onToggleDay: (day: BlockWeekDay) => void;
  disabled?: boolean;
}

const LABEL_CLS =
  'block text-xs font-medium text-text-muted mb-1';

const PERIOD_OPTIONS: { value: BlockRecurringPeriod; label: string }[] = [
  // Wire value is plural ("Days"); Flutter renders the singular label
  // (`recurring_widget.dart:139` strips the trailing 's').
  { value: BLOCK_PERIOD_DAYS, label: 'Day' },
  { value: BLOCK_PERIOD_WEEKS, label: 'Week' },
  { value: BLOCK_PERIOD_MONTHS, label: 'Month' },
];

function formatDate(d: Date | null): string {
  if (!d) return 'Select end date';
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function BlockAvailabilityRecurringPicker({
  recurring,
  blockDate,
  onPatch,
  onChangePeriod,
  onToggleDay,
  disabled = false,
}: Props) {
  const [calendarOpen, setCalendarOpen] = useState(false);

  // End-date upper bound: today + 5yrs. Flutter uses `today + 100yrs`
  // (`recurring_widget.dart:275`) but the popover calendar's month-
  // by-month nav makes 100yrs unusable.
  //
  // Lower bound (Samer review §2): max(blockDate, today). Flutter
  // uses `today` literally (`:274`) and React originally mirrored
  // blockDate; both shapes silently allow a past end date through
  // when blockDate is in the past (edit flow, timezone edge cases).
  // Defensive max keeps the recurring window from ever closing
  // before the moment the user is currently making the decision.
  const today = startOfDay(new Date());
  const lastDate = startOfDay(new Date());
  lastDate.setFullYear(lastDate.getFullYear() + 5);
  const blockDayFloor = startOfDay(blockDate);
  const endDateFloor = blockDayFloor.getTime() > today.getTime() ? blockDayFloor : today;

  const endDate = recurring.endDate ? new Date(recurring.endDate) : null;

  // PROD-2528 — the month the calendar OPENS on. Left to itself DayPicker opens
  // on today's month, and for a block weeks ahead every day on that screen is
  // below `endDateFloor`, so the picker opened on a month with nothing
  // clickable. Open on the chosen end date when there is one, else on the
  // first selectable day. `defaultMonth` (not a controlled `month`) so the
  // arrows and dropdowns still page freely; it is re-read on every open
  // because the popover unmounts its content when it closes.
  const initialMonth = endDate ?? endDateFloor;

  const handleDecrement = () => {
    const cur = recurring.repeatEvery ?? 1;
    if (cur > 1) onPatch({ repeatEvery: cur - 1 });
  };
  const handleIncrement = () => {
    const cur = recurring.repeatEvery ?? 0;
    onPatch({ repeatEvery: cur + 1 });
  };

  // SelectionGroup hands back the full next array; diff it to the one day
  // that flipped and route through the parent's onToggleDay so its existing
  // toggle logic / side-effects (weekday auto-seed on period flip) are
  // preserved exactly.
  const handleDaysChange = (next: string[]) => {
    const cur = recurring.days;
    const changed =
      next.find((d) => !cur.includes(d)) ?? cur.find((d) => !next.includes(d));
    // `changed` originates from BLOCK_WEEK_DAYS values, so it's a valid
    // BlockWeekDay — the wire union is narrower than the string[] form state.
    if (changed) onToggleDay(changed as BlockWeekDay);
  };

  const showWeekDays = recurring.period === BLOCK_PERIOD_WEEKS;
  const showMonthInfo = recurring.period === BLOCK_PERIOD_MONTHS;

  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-text-muted">
        Recurring
      </p>

      {/* REPEAT EVERY stepper — Flutter `recurring_widget.dart:80-122`. */}
      <div>
        <label className={LABEL_CLS}>Repeat every</label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDecrement}
            disabled={disabled || (recurring.repeatEvery ?? 0) <= 1}
            aria-label="Decrement"
            className="h-10 w-10 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-primary disabled:bg-grey-100 disabled:text-text-muted hover:border-brand-primary"
          >
            <Minus className="h-4 w-4 mx-auto" />
          </button>
          <div className="h-10 w-14 flex items-center justify-center rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-sm font-medium text-text-primary">
            {recurring.repeatEvery ?? 0}
          </div>
          <button
            type="button"
            onClick={handleIncrement}
            disabled={disabled}
            aria-label="Increment"
            className="h-10 w-10 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-primary disabled:bg-grey-100 disabled:text-text-muted hover:border-brand-primary"
          >
            <Plus className="h-4 w-4 mx-auto" />
          </button>
        </div>
      </div>

      {/* PERIOD picker — Signal Segmented (exclusive choice). Flutter
          `recurring_widget.dart:125-161`. Read-only edit mode disables each
          segment (Segmented has no top-level disabled prop). */}
      <div>
        <label className={LABEL_CLS}>Period</label>
        <Segmented
          aria-label="Period"
          options={PERIOD_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled,
          }))}
          value={recurring.period ?? undefined}
          onChange={(v) => onChangePeriod(v as BlockRecurringPeriod)}
        />
      </div>

      {/* REPEAT ON weekday chips — Signal SelectionGroup (multi-select).
          Flutter `recurring_widget.dart:165-241`. Only when period === Weeks. */}
      {showWeekDays && (
        <div>
          <label className={LABEL_CLS}>Repeat on</label>
          <SelectionGroup<string>
            aria-label="Repeat on"
            options={BLOCK_WEEK_DAYS.map((day) => ({ value: day, label: day[0] }))}
            value={recurring.days}
            onChange={handleDaysChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* MONTHLY info — Flutter `recurring_widget.dart:242-258`. */}
      {showMonthInfo && (
        <p className="[font:var(--type-body)] text-[var(--ink-meta)]">
          Every 4 weeks (exactly 28 days).
        </p>
      )}

      {/* END DATE — Flutter `recurring_widget.dart:262-289`. */}
      <div>
        <label className={LABEL_CLS}>
          End date <span className="text-[var(--status-bad)]">*</span>
        </label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          {/* Base UI PopoverTrigger renders its own <button>; styling
              directly (not via asChild — Radix idiom, breaks here). */}
          <PopoverTrigger
            disabled={disabled}
            className={cn(
              'w-full h-10 px-3 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-left text-sm flex items-center justify-between',
              endDate ? 'text-text-primary' : 'text-text-hint',
              disabled && 'bg-grey-100 text-text-muted cursor-not-allowed',
            )}
          >
            <span>{formatDate(endDate)}</span>
            <ChevronDown className="h-4 w-4 text-text-muted flex-shrink-0" />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <Calendar
              mode="single"
              selected={endDate ?? undefined}
              onSelect={(d) => {
                if (d) {
                  // Store as ISO no-Z date-only string in the form state;
                  // the wire serializer combines with the End time.
                  // Use local components so the picked day isn't
                  // shifted by timezone on save.
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  onPatch({ endDate: `${y}-${m}-${day}T00:00:00.000` });
                  setCalendarOpen(false);
                }
              }}
              // Floor = max(blockDate, today) (Samer §2). Ceiling = today + 5yrs.
              disabled={(d) => d < endDateFloor || d > lastDate}
              defaultMonth={initialMonth}
              initialFocus
            />
            {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value.
                Today is disabled when it falls before the floor (future blockDate). */}
            <div className="mt-2 flex items-center justify-between border-t border-[var(--surface-line)] pt-2 text-size-sm">
              <button
                type="button"
                disabled={today.getTime() < endDateFloor.getTime()}
                onClick={() => {
                  const y = today.getFullYear();
                  const m = String(today.getMonth() + 1).padStart(2, '0');
                  const day = String(today.getDate()).padStart(2, '0');
                  onPatch({ endDate: `${y}-${m}-${day}T00:00:00.000` });
                  setCalendarOpen(false);
                }}
                className="font-medium text-brand-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
              >
                Today
              </button>
              <span className="text-text-muted">{endDate ? formatDate(endDate) : '—'}</span>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
